import { Injectable, inject } from '@angular/core';
import { type HttpErrorResponse } from '@angular/common/http';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { forkJoin, of } from 'rxjs';
import { map, mergeMap, switchMap, catchError } from 'rxjs/operators';

import { PageSettingsHttpService } from '@web/store/website/page-settings/page-settings.http.service';
import { PagesHttpService } from '@web/store/website/pages/pages.http.service';

import { PagesService } from '@web/store/website/pages/pages.service';

import PagesActions from '@web/store/website/pages/pages.actions';
import PageSettingsActions from './page-settings.actions';

@Injectable()
export class PageSettingsEffects {
    private readonly actions$ = inject(Actions);
    private readonly pageSettingsHttpService = inject(PageSettingsHttpService);
    private readonly pagesHttpService = inject(PagesHttpService);
    private readonly pagesService = inject(PagesService);

    public readonly loadPageSettingsRequest$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PageSettingsActions.loadPageSettingsRequest),
            mergeMap(({ siteId, pageId }) => {
                return this.pageSettingsHttpService.getSitePageSettings(siteId, pageId).pipe(
                    map((settings) => {
                        return PageSettingsActions.loadPageSettingsSuccess({ pageId, settings });
                    })
                );
            })
        )
    );

    public readonly updatePageSettingsRequest$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PageSettingsActions.updatePageSettingsRequest),
            switchMap(({ siteId, pageId, settings }) =>
                this.pageSettingsHttpService.updatePageSettings(siteId, pageId, settings).pipe(
                    mergeMap((pageSettings) =>
                        forkJoin({
                            pageSettings: of(pageSettings),
                            page: this.pagesHttpService.getSitePage(siteId, pageId),
                        })
                    ),
                    switchMap(({ pageSettings, page }) => [
                        PageSettingsActions.updatePageSettingsSuccess({
                            siteId,
                            pageId,
                            pageSettings,
                        }),
                        PagesActions.updatePage({ pageId, page: this.pagesService.mapPageToEntityPage(page) }),
                    ]),
                    catchError(({ message }: HttpErrorResponse) => [
                        PageSettingsActions.updatePageSettingsFailure({ siteId, pageId, error: message }),
                    ])
                )
            )
        )
    );
}
